import { Chunk } from './chunks';
import { CHUNK_SIZE } from './grid';
import { Level, PropDef } from './level';
import { courseTapeStakes } from './furniture';
import { paddockBoxes } from './paddockLayout';
import { SCATTER_MODELS, scatterChunk, STONE_MODEL } from './scatter';

/**
 * Static things the bike can hit: tree trunks, rocks and stones, straw bales, cones, gate poles, course-tape stakes
 * and paddock props. Everything is generated from the same deterministic data the view draws from (chunk trees,
 * src/sim/scatter.ts, level props), never from the scene graph.
 *
 * Storage is struct-of-arrays in growable typed arrays; a uniform grid (CELL m) answers "what is near here" without
 * allocating: `query` fills a reused index buffer.
 */
export const Shape = { Cylinder: 0, Dome: 1, Box: 2 } as const;
export type Shape = (typeof Shape)[keyof typeof Shape];
export type ColliderKind = 'tree' | 'rock' | 'stone' | 'hay' | 'cone' | 'pole' | 'stake' | 'prop';
export const KINDS: readonly ColliderKind[] = ['tree', 'rock', 'stone', 'hay', 'cone', 'pole', 'stake', 'prop'];

/** How each kind behaves when hit. */
export const KIND_FEEL: Record<ColliderKind, { shape: Shape; stiffness: number; knock: boolean; bump: boolean }> = {
  tree: { shape: Shape.Cylinder, stiffness: 1, knock: false, bump: false },
  rock: { shape: Shape.Dome, stiffness: 1, knock: false, bump: true },
  stone: { shape: Shape.Dome, stiffness: 1, knock: false, bump: true },
  hay: { shape: Shape.Box, stiffness: 0.35, knock: false, bump: true },   // straw gives
  cone: { shape: Shape.Cylinder, stiffness: 0, knock: true, bump: false }, // knocked over, no push
  pole: { shape: Shape.Cylinder, stiffness: 0.6, knock: false, bump: false },
  stake: { shape: Shape.Cylinder, stiffness: 0.15, knock: false, bump: false }, // a tape stake snaps / bends
  prop: { shape: Shape.Box, stiffness: 1, knock: false, bump: false },
};

const CELL = 4;
const TREE_HEIGHT = 12;
// Small-integer keys (under 2^30, cells within +-65 km): past the Smi range a key is a boxed number, an allocation per lookup.
const key = (i: number, j: number) => (i + 16384) * 32768 + (j + 16384);

export class ObstacleField {
  count = 0;
  kind = new Uint8Array(256);
  x = new Float64Array(256);
  z = new Float64Array(256);
  y = new Float64Array(256);   // base height (ground under the obstacle)
  h = new Float64Array(256);   // height above the base
  a = new Float64Array(256);   // radius (cylinder), or half extent along the local x (dome, box)
  b = new Float64Array(256);   // half extent along the local z (dome, box)
  cos = new Float64Array(256); // local x axis = (cos, -sin) in world x/z (three.js rotation about +y)
  sin = new Float64Array(256);
  knocked = new Uint8Array(256);
  /** Largest horizontal reach of any collider (for broadphase margins). */
  maxReach = 0;
  private stamp = new Uint32Array(256);
  private queryId = 0;
  private readonly cells = new Map<number, number[]>();
  private readonly chunksDone = new Set<number>();
  /** Reused result buffer of `query`. */
  hits = new Int32Array(64);

  /**
   * `chunkAt` (optional) supplies terrain chunks: their trees and scatter are added the first time a query touches
   * them. Level props, course-tape stakes and paddock props are added up front.
   */
  constructor(readonly level: Level | null = null, private readonly chunkAt: ((x: number, z: number) => Chunk) | null = null,
    heightAt: (x: number, z: number) => number = () => 0) {
    if (level) addLevelColliders(this, level, heightAt);
  }

  /** Adds one collider; returns its index. */
  add(kind: ColliderKind, x: number, z: number, y: number, h: number, a: number, b = a, yaw = 0): number {
    if (this.count === this.kind.length) this.grow();
    const i = this.count++;
    this.kind[i] = KINDS.indexOf(kind);
    this.x[i] = x; this.z[i] = z; this.y[i] = y; this.h[i] = h; this.a[i] = a; this.b[i] = b;
    this.cos[i] = Math.cos(yaw); this.sin[i] = Math.sin(yaw);
    const reach = Math.max(a, b);
    this.maxReach = Math.max(this.maxReach, reach);
    for (let ci = Math.floor((x - reach) / CELL); ci <= Math.floor((x + reach) / CELL); ci++) {
      for (let cj = Math.floor((z - reach) / CELL); cj <= Math.floor((z + reach) / CELL); cj++) {
        const k = key(ci, cj);
        let list = this.cells.get(k);
        if (!list) { list = []; this.cells.set(k, list); }
        list.push(i);
      }
    }
    return i;
  }

  private grow(): void {
    const n = this.kind.length * 2;
    const g = <T extends Float64Array | Uint8Array | Uint32Array>(a: T, C: new (n: number) => T): T => { const b = new C(n); b.set(a); return b; };
    this.kind = g(this.kind, Uint8Array); this.knocked = g(this.knocked, Uint8Array); this.stamp = g(this.stamp, Uint32Array);
    this.x = g(this.x, Float64Array); this.z = g(this.z, Float64Array); this.y = g(this.y, Float64Array); this.h = g(this.h, Float64Array);
    this.a = g(this.a, Float64Array); this.b = g(this.b, Float64Array); this.cos = g(this.cos, Float64Array); this.sin = g(this.sin, Float64Array);
  }

  /** Trees and scatter of the chunk under (x, z), once. */
  /** Chunk (ci, cj) (integers: the per-step query passes no boxed doubles). */
  private ensureChunk(ci: number, cj: number): void {
    if (!this.chunkAt) return;
    const k = key(ci, cj);
    if (this.chunksDone.has(k)) return;
    this.chunksDone.add(k);
    const c = this.chunkAt((ci + 0.5) * CHUNK_SIZE, (cj + 0.5) * CHUNK_SIZE);
    const t = c.trees;
    if (t) {
      for (let n = 0; n < t.radius.length; n++) {
        const r = t.radius[n]!;
        if (r > 0) this.add('tree', t.xs[n]!, t.zs[n]!, c.field.heightAt(t.xs[n]!, t.zs[n]!) - 0.5, TREE_HEIGHT, r);
      }
    }
    if (this.level) {
      for (const it of scatterChunk(this.level, c)) {
        const m = SCATTER_MODELS[it.model]!;
        this.add(it.model === STONE_MODEL ? 'stone' : 'rock', it.x, it.z, it.y, m.h * it.scale, m.hx * it.scale, m.hz * it.scale, it.yaw);
      }
    }
  }

  /**
   * Indices of the colliders whose cells touch the square of half-size `r` round (x, z), into `this.hits`; returns
   * how many. No allocation once warmed up (the hit buffer only grows).
   */
  query(x: number, z: number, r: number): number {
    const x0 = x - r, x1 = x + r, z0 = z - r, z1 = z + r;
    const a0 = Math.floor(x0 / CHUNK_SIZE), a1 = Math.floor(x1 / CHUNK_SIZE), b0 = Math.floor(z0 / CHUNK_SIZE), b1 = Math.floor(z1 / CHUNK_SIZE);
    this.ensureChunk(a0, b0); this.ensureChunk(a1, b0); this.ensureChunk(a0, b1); this.ensureChunk(a1, b1);
    const q = ++this.queryId;
    let n = 0;
    for (let ci = Math.floor(x0 / CELL); ci <= Math.floor(x1 / CELL); ci++) {
      for (let cj = Math.floor(z0 / CELL); cj <= Math.floor(z1 / CELL); cj++) {
        const list = this.cells.get(key(ci, cj));
        if (!list) continue;
        for (let k = 0; k < list.length; k++) {
          const i = list[k]!;
          if (this.stamp[i] === q) continue;
          this.stamp[i] = q;
          if (n === this.hits.length) { const b = new Int32Array(n * 2); b.set(this.hits); this.hits = b; }
          this.hits[n++] = i;
        }
      }
    }
    return n;
  }

  /** Height of collider i's top surface at (px, pz), or -Infinity outside its footprint (domes and boxes only). */
  topAt(i: number, px: number, pz: number): number {
    const shape = KIND_FEEL[KINDS[this.kind[i]!]!].shape;
    const dx = px - this.x[i]!, dz = pz - this.z[i]!;
    // Into the collider's frame (rotation about +y by yaw: local x = (cos, -sin)).
    const lx = dx * this.cos[i]! - dz * this.sin[i]!, lz = dx * this.sin[i]! + dz * this.cos[i]!;
    const u = lx / this.a[i]!, v = lz / this.b[i]!;
    if (shape === Shape.Dome) {
      const q = u * u + v * v;
      return q >= 1 ? -Infinity : this.y[i]! + this.h[i]! * Math.sqrt(1 - q);
    }
    if (shape === Shape.Box) return Math.abs(u) <= 1 && Math.abs(v) <= 1 ? this.y[i]! + this.h[i]! : -Infinity;
    return -Infinity;
  }
}

/** On the riding surface of the lap (a start-gate post, or furniture that fell onto another stretch in a hairpin). */
export const onLap = (level: Level, x: number, z: number) => {
  if (!level.line) return false;
  const c = level.line.closest(x, z);
  return Math.abs(c.d) <= level.halfWidthAt[c.index]! + 0.05; // sections differ in width
};

/** Level props, course-tape stakes and the paddock: the colliders that do not come from terrain chunks. */
function addLevelColliders(f: ObstacleField, level: Level, heightAt: (x: number, z: number) => number): void {
  const props: PropDef[] = [...level.def.props, ...(level.def.scenery ?? [])];
  for (const p of props) {
    if (p.kind === 'cone') f.add('cone', p.x, p.z, heightAt(p.x, p.z), 0.45, 0.16);
    // A row of posts across the lap is the start gate: it drops for the start, so it is ridden over, not hit.
    else if (p.kind === 'pole') { if (!onLap(level, p.x, p.z)) f.add('pole', p.x, p.z, heightAt(p.x, p.z), 1.8, 0.03); }
    // Straw bale: 1.1 x 0.5 x 0.45 m box turned by `heading` (as src/view/props.ts draws it).
    // (A bale or stake that ends up on another stretch of the lap, inside a hairpin, has been knocked flat by the riders.)
    else if (p.kind === 'hay') { if (!onLap(level, p.x, p.z)) f.add('hay', p.x, p.z, heightAt(p.x, p.z), 0.5, 0.55, 0.225, p.heading); }
  }
  // Course tape: the same stakes the view draws (src/sim/furniture.ts).
  if (level.line && (level.def.courseTape ?? !level.def.forest)) {
    for (const side of courseTapeStakes(level) ?? []) for (const p of side) if (p) f.add('stake', p.x, p.z, heightAt(p.x, p.z), 0.9, 0.025);
  }
  addPaddock(f, level, heightAt);
}

/** The school paddock: boxes from the layout the view draws (src/sim/paddockLayout.ts). */
function addPaddock(f: ObstacleField, level: Level, heightAt: (x: number, z: number) => number): void {
  for (const b of paddockBoxes(level)) f.add('prop', b.x, b.z, heightAt(b.x, b.z), b.h, b.hx, b.hz, b.yaw);
}
